import { createActionGroup, emptyProps, props } from '@ngrx/store'

import {
  ActionDefinition,
  NodeDefinition,
} from '@flowx/designer/modules/core/dictionary/process.dictionary'
import { ActionProgressEvent } from '@flowx/designer/modules/ai/common/api/conversation-stream.types'

import {
  AiFeedback,
  AiPermissions,
  BusinessRuleContext,
  FeedbackType,
  Position,
} from '../types/ai.types'

export const AiActions = createActionGroup({
  source: 'AiGeneral',
  events: {
    fabClick: emptyProps(),
    outsideOfAiChatClick: emptyProps(),

    fromDocumentsClick: emptyProps(),
    maxFileSizeExceeded: props<{ maxFileSize: number }>(),

    contextDocumentCloseClick: emptyProps(),
    closeContextDocumentConfirmed: emptyProps(),

    closeChatConfirmed: emptyProps(),

    draftInfoClick: emptyProps(),

    canvasTransform: props<{
      generatedProcessPreviewHeader: {
        containerTransform: string
        contentTransform: string
      }
    }>(),

    actionEditDestroyed: emptyProps(),

    businessRuleContextChanged: props<{ businessRuleContext: BusinessRuleContext | null }>(),
    businessRuleContextReset: props<{ businessRuleContext: BusinessRuleContext | null }>(),

    feedbackClick: props<{ requestId: string; feedbackType: FeedbackType }>(),
    sendFeedback: props<{ feedback: AiFeedback }>(),

    fabMoved: props<{ position: Position }>(),

    cancelPendingRequestClick: emptyProps(),
    cancelPendingRequest: emptyProps(),

    pageAgentChanged: emptyProps(),

    enterCommandCenter: emptyProps(),
    exitCommandCenter: emptyProps(),
  },
})

export const FloatingWindowActions = createActionGroup({
  source: 'FloatingWindow',
  events: {
    minimizeClick: emptyProps(),
  },
})

export const BreadcrumbActions = createActionGroup({
  source: 'Breadcrumbs',
  events: {
    backClick: emptyProps(),
  },
})

export const CommandPaletteActions = createActionGroup({
  source: 'CommandPalette',
  events: {
    generateNewProcessCommand: emptyProps(),
    generateExistingProcessCommand: emptyProps(),
    generateNewUICommand: props<{ userTaskNavigationAreaId: number }>(),
    generateNewBusinessRuleCommand: emptyProps(),
    askAssistantCommand: props<{ question: string }>(),
  },
})

export const CtaActions = createActionGroup({
  source: 'CTA',
  events: {
    processDesignerCtaClick: emptyProps(),
    uiDesignerCtaClick: props<{ userTaskNavigationAreaId: number }>(),
    fixBusinessRuleWarningsCtaClick: emptyProps(),
    fixBusinessRuleErrorCtaClick: emptyProps(),
    explainBusinessRuleCtaClick: emptyProps(),
    generateBusinessRuleCtaClick: props<{
      nodeId: NodeDefinition['id']
      actionId: ActionDefinition['id']
      businessRuleIndex: number
      code: string
      language: string
    }>(),
  },
})

export const KeyboardActions = createActionGroup({
  source: 'Keyboard',
  events: {
    escape: props<{ isBreadcrumbsBackDisabled?: boolean }>(),
    searchShortcut: emptyProps(),
    generateShortcut: emptyProps(),
  },
})

export const AiPermissionActions = createActionGroup({
  source: 'AiPermissions',
  events: {
    loadAiPermissionsSuccess: props<{ permissions: AiPermissions }>(),
  },
})

export const NodeMenuActions = createActionGroup({
  source: 'NodeMenuActions',
  events: {
    generateProcessNodeMenuAction: emptyProps(),
    generateUiNodeMenuAction: props<{ userTaskNavigationAreaId: number }>(),
    generateUiNodeMenuActionTransitionEnd: props<{ userTaskNavigationAreaId: number }>(),
    generateBusinessRuleNodeMenuAction: emptyProps(),
    generateBusinessRuleNodeMenuActionTransitionEnd: emptyProps(),
  },
})

export const SequenceMenuActions = createActionGroup({
  source: 'SequenceMenuActions',
  events: {
    generateProcess: props<{ sequenceId: number }>(),
  },
})

export const BusinessRuleEditorActions = createActionGroup({
  source: 'BusinessRuleEditor',
  events: {
    cancelFixWarnings: emptyProps(),
    cancelFixError: emptyProps(),
  },
})

export const AiConversationServiceActions = createActionGroup({
  source: 'AiConversationService',
  events: {
    conversationStarted: props<{
      conversationId: string
      threadId: string
      lastMessageId: string
    }>(),
    conversationCreating: emptyProps(),
    conversationCreated: emptyProps(),
    threadAdd: props<{
      threadId: string
      lastMessageId: string
    }>(),
    threadMessageAdd: props<{
      messageId: string
    }>(),
    conversationMessageStreamEnded: props<{
      messageId: string
      conversationId: string
      threadId: string
    }>(),
    conversationMessageStreamSuccess: props<{
      messageId: string
      conversationId: string
      threadId: string
    }>(),
    conversationMessageStreamError: props<{
      messageId: string
      threadId: string
      conversationId: string
    }>(),
    aiConversationStreamUpdate: props<{
      updates: ActionProgressEvent
    }>(),
    executionPlanActionListenStarted: emptyProps(),
  },
})
